import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { InputEmails } from '@/components/ui/input-emails';
import { GET_ONBOARDING_V2_VARIANT } from '@/features/flags';
import { inviteTeamStepCompletedAtom } from '@/features/growth/practice-invite-team-members/atoms';
import { usePracticeInviteEmailForm } from '@/features/growth/practice-invite-team-members/usePracticeInviteEmailForm';
import { useOnboardingContext } from '@/features/onboarding-v2/context/OnboardingContext';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { Button } from '@heidi/design-system/button';
import { Label } from '@heidi/design-system/label';
import { TypographyH2, TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useSetAtom } from 'jotai';
import { Mail } from 'lucide-react';

import { OnboardingStepShell } from '@heidi/growth';
import { inviteTeamStepMessages } from './invite-team-step.messages';
import { InviteEmailTextarea } from './InviteEmailTextarea';

const TOUR_EMAIL_FIELD_ID = 'onboarding-tour-invite-emails';

/**
 * Collects invite emails only. Send is deferred to `completeOnboarding` after
 * the organisation_info PATCH provisions the Practice team.
 */
export function InviteTeamStep() {
  const intl = useIntl();
  const {
    analytics,
    currentStep,
    currentStepIndex,
    formData,
    goToNextStep,
    updateFormData,
  } = useOnboardingContext();
  const setInviteTeamStepCompleted = useSetAtom(inviteTeamStepCompletedAtom);
  const invalidEmailMessage = intl.formatMessage(inviteTeamStepMessages.invalidEmail);
  const { emails, errorMessage, failedEmailsData, onEmailsChange, validateEmails } =
    usePracticeInviteEmailForm(invalidEmailMessage);
  const [areEmailsValid, setAreEmailsValid] = useState(false);
  // The guided-tour arm renders this same component as `tour-invite-team`, at an
  // earlier position and against its own Figma frame.
  const isGuidedTour = currentStep === 'tour-invite-team';
  const continueMessage = isGuidedTour
    ? inviteTeamStepMessages.sendInvites
    : inviteTeamStepMessages.continue;
  const skipMessage = isGuidedTour
    ? inviteTeamStepMessages.skipForNow
    : inviteTeamStepMessages.skip;
  // Sending nothing is what Skip is for, so the tour's primary stays disabled on
  // an empty field rather than falling through to the empty-list error.
  const isContinueDisabled = isGuidedTour
    ? emails.length === 0 || !areEmailsValid
    : emails.length > 0 && !areEmailsValid;

  const handleEmailsChange = useCallback(
    (value: Parameters<typeof onEmailsChange>[0]) => {
      onEmailsChange((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        updateFormData('inviteEmails', next);
        return next;
      });
    },
    [onEmailsChange, updateFormData]
  );

  // Once per mount; tag with onboarding A/B variant for funnel split.
  const hasTrackedScreenView = useRef(false);
  useEffect(() => {
    if (hasTrackedScreenView.current) return;
    hasTrackedScreenView.current = true;
    track({
      event_name: TrackingEventName.ScreenViewed,
      props: {
        screen_name: ScreenName.OnboardingInviteTeam,
        onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
      },
    });
  }, []);

  // Re-seed from form when returning via Back from completion.
  const hasSeededEmails = useRef(false);
  useEffect(() => {
    if (hasSeededEmails.current) return;
    hasSeededEmails.current = true;
    if (formData.inviteEmails.length > 0) {
      handleEmailsChange(formData.inviteEmails);
    }
  }, [formData.inviteEmails, handleEmailsChange]);

  // `handleContinue` awaits validation before leaving, so a second click can
  // land while the first is still in flight — that would report two
  // step_completed events and advance the wizard twice.
  const hasLeftStep = useRef(false);
  const leaveStep = useCallback(
    (skipped: boolean, nextEmails: string[]) => {
      if (hasLeftStep.current) return;
      hasLeftStep.current = true;
      // Report the live step id, not a literal: the guided-tour arm renders this
      // same component as `tour-invite-team` at an earlier position, and the
      // funnel needs to tell the two apart.
      if (skipped) {
        analytics.trackStepSkipped(currentStep);
      } else {
        analytics.trackStepCompleted(currentStep, currentStepIndex + 1);
      }
      updateFormData('inviteEmails', nextEmails);
      // Step was shown — mark done so PracticeInviteTeamModal does not
      // double-prompt after the Practice trial modal.
      setInviteTeamStepCompleted(true);
      goToNextStep();
    },
    [
      analytics,
      currentStep,
      currentStepIndex,
      goToNextStep,
      setInviteTeamStepCompleted,
      updateFormData,
    ]
  );

  const handleSkip = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.OnboardingInviteTeam,
        action: ButtonAction.PracticeInviteSkip,
        label: intl.formatMessage(skipMessage),
      },
    });
    leaveStep(true, []);
  }, [intl, leaveStep, skipMessage]);

  const handleContinue = useCallback(async () => {
    const validated = await validateEmails();
    if (!validated) return;

    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.OnboardingInviteTeam,
        action: ButtonAction.PracticeInviteContinue,
        label: intl.formatMessage(continueMessage),
      },
    });

    leaveStep(false, validated);
  }, [continueMessage, intl, leaveStep, validateEmails]);

  const heading = (
    <div className="flex flex-col gap-2">
      <TypographyH2>
        <FormattedMessage {...inviteTeamStepMessages.title} />
      </TypographyH2>
      <TypographyP className="text-text-secondary">
        <FormattedMessage {...inviteTeamStepMessages.subtitle} />
      </TypographyP>
    </div>
  );

  const errorText = errorMessage ? (
    <TypographyP2 className="text-validation-destructive">{errorMessage}</TypographyP2>
  ) : null;

  // Treatment arm only. Its Figma frame collects the whole list in one
  // free-text box, and the step sits at `tour-invite-team` where a 640px-tall
  // viewport has to keep the actions reachable — hence the tour shell: content
  // scrolls, footer stays put. The control arm keeps the chip field and its
  // current layout so the experiment only moves the step.
  if (isGuidedTour) {
    return (
      <OnboardingStepShell
        contentClassName="gap-10"
        footer={
          // Wraps rather than clips when a translation runs long in the 400px
          // column.
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="lg" onClick={handleSkip}>
              <FormattedMessage {...skipMessage} />
            </Button>
            <Button
              type="button"
              size="lg"
              disabled={isContinueDisabled}
              onClick={() => void handleContinue()}
            >
              <FormattedMessage {...continueMessage} />
            </Button>
          </div>
        }
      >
        {heading}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={TOUR_EMAIL_FIELD_ID}>
            <FormattedMessage {...inviteTeamStepMessages.teamLabel} />
          </Label>
          <InviteEmailTextarea
            id={TOUR_EMAIL_FIELD_ID}
            emails={emails}
            onChange={handleEmailsChange}
            onValidChange={setAreEmailsValid}
            hasError={!!errorMessage}
          />
          {errorText}
        </div>
      </OnboardingStepShell>
    );
  }

  return (
    <div className="flex h-full flex-col gap-10">
      {heading}

      <div className="flex flex-col gap-1.5">
        <TypographyP2>
          <FormattedMessage {...inviteTeamStepMessages.emailLabel} />
        </TypographyP2>
        <InputEmails
          value={emails}
          onChange={handleEmailsChange}
          onValidChange={setAreEmailsValid}
          limit={100}
          placeholder={intl.formatMessage(inviteTeamStepMessages.emailPlaceholder)}
          accessoryStart={<Mail size={16} className="text-text-secondary" aria-hidden />}
          failedEmailsData={failedEmailsData}
          inputMode="email"
          autoComplete="off"
          aria-invalid={!!errorMessage}
        />
        {errorText}
      </div>

      <div className="flex flex-1 items-end justify-end gap-3 pt-4">
        <Button type="button" variant="ghost" size="lg" onClick={handleSkip}>
          <TypographyP>
            <FormattedMessage {...skipMessage} />
          </TypographyP>
        </Button>
        <Button
          type="button"
          size="lg"
          disabled={isContinueDisabled}
          onClick={() => void handleContinue()}
        >
          <TypographyP>
            <FormattedMessage {...continueMessage} />
          </TypographyP>
        </Button>
      </div>
    </div>
  );
}
